import type { MemberRole } from "./types";

const RANK: Record<MemberRole, number> = { viewer: 0, developer: 1, admin: 2, owner: 3 };

/** Two initials from a name — "Ada Lovelace" → "AL", "Grace" → "G". */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * Owners first, then admins, then developers, then viewers; newest member
 * first within a role. The person who runs the account is the person you scan
 * for, so they sit where the eye lands.
 */
export function sortMembers<M extends { role: MemberRole; joinedAt: string }>(members: M[]): M[] {
  return [...members].sort(
    (a, b) =>
      RANK[b.role] - RANK[a.role] || Date.parse(b.joinedAt) - Date.parse(a.joinedAt),
  );
}

/** Case-insensitive match on name or email. */
export function filterMembers<M extends { name: string; email: string }>(
  members: M[],
  query: string,
): M[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return members;
  return members.filter(
    (member) =>
      member.name.toLowerCase().includes(needle) || member.email.toLowerCase().includes(needle),
  );
}